import { SvgIconProps, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridActionsCellItemProps } from "@mui/x-data-grid-premium";
import { ComponentType, ReactElement } from "react";

import { LINKED_SDS_NO_PERMISSION_MESSAGE } from "./canModifyLinkedSds";

// MUI's disabled-state opacity, so a locked menu item reads like a disabled one.
const DISABLED_OPACITY = 0.38;

/**
 * A row action that's locked when the document is an SDS on a chemical the
 * user can't write. Locked actions stay enabled with aria-disabled, because
 * `disabled` would block the tooltip that explains why, and onClick is skipped.
 *
 * Returns a GridActionsCellItem element (not a wrapper component) so the grid
 * can inject its focus props.
 */
export function buildLinkedSdsAction({
  key,
  label,
  Icon,
  canModify,
  showInMenu,
  disabled,
  onClick,
}: {
  key: string;
  label: string;
  Icon: ComponentType<SvgIconProps>;
  canModify: boolean;
  showInMenu: boolean;
  disabled?: boolean;
  onClick: () => void;
}): ReactElement<GridActionsCellItemProps> {
  const handleClick = () => {
    if (canModify) {
      onClick();
    }
  };

  if (showInMenu) {
    return (
      <GridActionsCellItem
        key={key}
        showInMenu
        icon={<Icon />}
        label={
          canModify ? (
            label
          ) : (
            <Tooltip title={LINKED_SDS_NO_PERMISSION_MESSAGE} placement="left" describeChild>
              <span>{label}</span>
            </Tooltip>
          )
        }
        aria-disabled={canModify ? undefined : true}
        style={canModify ? undefined : { opacity: DISABLED_OPACITY }}
        closeMenuOnClick={canModify}
        disabled={disabled}
        onClick={handleClick}
      />
    );
  }

  return (
    <GridActionsCellItem
      key={key}
      label={label}
      icon={
        <Tooltip title={canModify ? label : LINKED_SDS_NO_PERMISSION_MESSAGE} describeChild>
          <Icon color={canModify ? undefined : "disabled"} />
        </Tooltip>
      }
      aria-disabled={canModify ? undefined : true}
      disabled={disabled}
      onClick={handleClick}
    />
  );
}
